// src/components/facility/panels/ResearchPanel.js
import React from 'react';
import { TECH_NODES, TECH_BRANCHES } from '../../../game/data/technology';
import { ProgressBar } from '../../ui/ProgressBar';
import { calculateWeeklyRP } from '../../../game/systems/labor';

export function ResearchPanel({ state, dispatch }) {
  const rpPerWeek = calculateWeeklyRP(state.workers);
  const active = state.activeResearch;

  // Nodes available to start: all requires unlocked, not yet unlocked, not currently active
  const availableNodes = Object.values(TECH_NODES).filter(node => {
    if ((state.unlockedTech || []).includes(node.id)) return false;
    if (active?.nodeId === node.id) return false;
    if (node.minStage && state.stage < node.minStage) return false;
    return node.requires.every(r => (state.unlockedTech || []).includes(r));
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* RP stats */}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1, background: '#1a1810', border: '1px solid #2a2618', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>RP / Week</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--blue)' }}>{rpPerWeek}</div>
        </div>
        <div style={{ flex: 1, background: '#1a1810', border: '1px solid #2a2618', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Banked RP</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--purple)' }}>{state.researchPoints || 0}</div>
        </div>
      </div>

      {/* Active research */}
      {active ? (() => {
        const node = TECH_NODES[active.nodeId];
        const branch = node ? TECH_BRANCHES[node.branch] : null;
        const pct = node ? Math.min(100, Math.round((active.pointsAccumulated / active.pointsRequired) * 100)) : 0;
        const weeksLeft = rpPerWeek > 0 ? Math.ceil((active.pointsRequired - active.pointsAccumulated) / rpPerWeek) : '∞';
        return (
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Active Research</div>
            <div style={{ background: '#1a1810', border: '1px solid #2a2618', borderRadius: 6, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{node?.name || active.nodeId}</div>
                  {branch && <div style={{ fontSize: 11, color: branch.color }}>{branch.icon} {branch.name}</div>}
                </div>
                <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>
                  <div>{active.pointsAccumulated} / {active.pointsRequired} RP</div>
                  <div>~{weeksLeft} wk{weeksLeft !== 1 ? 's' : ''}</div>
                </div>
              </div>
              <ProgressBar value={pct} showLabel label={`${pct}% complete`} />
            </div>
          </div>
        );
      })() : (
        <div style={{ background: '#1a1810', border: '1px solid var(--yellow-dim)', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--yellow)', textAlign: 'center' }}>
          ● No active research. Queue one below.
        </div>
      )}

      {/* Available next nodes */}
      {availableNodes.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {active ? 'Available to Queue Next' : 'Available to Research'}
          </div>
          {availableNodes.map(node => {
            const branch = TECH_BRANCHES[node.branch];
            const weeks = rpPerWeek > 0 ? Math.ceil(node.rp / rpPerWeek) : '∞';
            const canAfford = state.cash >= node.cost;
            return (
              <div key={node.id} style={{ background: '#181610', border: '1px solid #2a2618', borderRadius: 6, padding: '8px 10px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{node.name}</div>
                  <div style={{ fontSize: 11, color: branch?.color || 'var(--text-muted)' }}>{branch?.icon} {branch?.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{node.rp} RP · ${node.cost.toLocaleString()} · ~{weeks}wk</div>
                </div>
                {!active && (
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!canAfford}
                    title={!canAfford ? `Need $${node.cost.toLocaleString()}` : ''}
                    onClick={() => dispatch({ type: 'START_RESEARCH', payload: { nodeId: node.id } })}
                  >
                    Start
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {availableNodes.length === 0 && !active && (
        <div style={{ fontSize: 12, color: 'var(--text-dim)', textAlign: 'center', padding: '8px 0' }}>
          All available research complete. Visit the Research tab for advanced options.
        </div>
      )}
    </div>
  );
}
